import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@sunwebgroup/uds-ui/button'
import { IconButton } from '@sunwebgroup/uds-ui/icon-button'
import { TopTitleBar } from '@sunwebgroup/uds-ui/top-title-bar'
import cn from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CMSMediaType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { useIntl } from '@/intl'
import { useThrottledCallback } from '@/scopes/common/components/hooks/useThrottledCallback'
import { Image } from '@/scopes/common/components/Image'
import { Video } from '../../../products/pages/components/HeroSection/Gallery/Video'
import styles from './FullScreenGallery.module.css'
import { FullScreenGalleryProps } from './FullScreenGallery.types'

export function FullScreenGallery({ startFromIndex = 0, onClose, media, onSettle, zoom = false, ...attrs }: FullScreenGalleryProps) {
  const intl = useIntl()
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, startIndex: startFromIndex })
  const [slidesInView, setSlidesInView] = useState<number[]>([])
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [activeVideoLink, setActiveVideoLink] = useState<string | undefined>(undefined)
  const [zoomScale, setZoomScale] = useState(1)
  const numberOfSlides = media ? media.length : 0
  const slideRef = useRef<HTMLDivElement>(null)
  const activeSlide = media ? media[currentSlideIndex] : undefined

  const ZOOM_STEP = 0.5
  const MAX_ZOOM = 3
  const MIN_ZOOM = 1

  const findSlidesInView = useCallback(() => {
    if (!emblaApi)
      return

    setSlidesInView((slidesInViewElements) => {
      if (slidesInViewElements.length === emblaApi.slideNodes().length) {
        emblaApi.off('slidesInView', findSlidesInView)
      }
      const inView = emblaApi.slidesInView().filter(index => !slidesInViewElements.includes(index))
      return slidesInViewElements.concat(inView)
    })
  }, [emblaApi, setSlidesInView])

  function navigate(direction: 'previous' | 'next') {
    if (direction === 'previous') {
      emblaApi?.scrollPrev()
    }
    else {
      emblaApi?.scrollNext()
    }
  }

  // Slider events
  const onSelect = useCallback(() => {
    if (emblaApi !== undefined) {
      const index = emblaApi.selectedScrollSnap()
      setCurrentSlideIndex(index)
      setZoomScale(MIN_ZOOM)
    }
  }, [emblaApi])

  const onScroll = useThrottledCallback(() => {
    setActiveVideoLink(undefined)
  }, 2000)

  const onSettleCallback = useCallback(() => {
    if (onSettle) {
      onSettle()
    }
  }, [])

  const showVideo = (link: string | undefined) => {
    setActiveVideoLink(link)
  }

  const centerSlideScroll = () => {
    requestAnimationFrame(() => {
      const el = slideRef.current
      if (el) {
        el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2
        el.scrollTop = (el.scrollHeight - el.clientHeight) / 2
      }
    })
  }

  const toggleZoomIn = () => {
    setZoomScale((prev) => {
      const next = Math.min(prev + ZOOM_STEP, MAX_ZOOM)
      emblaApi?.reInit({ watchDrag: next === MIN_ZOOM })
      return next
    })
    centerSlideScroll()
  }

  const toggleZoomOut = () => {
    setZoomScale((prev) => {
      const next = Math.max(prev - ZOOM_STEP, MIN_ZOOM)
      emblaApi?.reInit({ watchDrag: next === MIN_ZOOM })
      return next
    })
    centerSlideScroll()
  }

  useEffect(() => {
    if (emblaApi === undefined)
      return

    setCurrentSlideIndex(emblaApi.selectedScrollSnap())
    emblaApi.on('select', onSelect)
    emblaApi.on('scroll', onScroll)
    emblaApi.on('settle', onSettleCallback)
    emblaApi.on('slidesInView', findSlidesInView)
    emblaApi.reInit()

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('scroll', onScroll)
      emblaApi.off('settle', onSettleCallback)
      emblaApi.off('slidesInView', findSlidesInView)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run once embla is mounted
  }, [emblaApi])

  const handlePauseOrEnded = () => {
    setActiveVideoLink(undefined)
  }

  const handleClose = () => {
    setActiveVideoLink(undefined)
    if (onClose) {
      onClose()
    }
  }

  return (
    <Dialog.Root {...attrs} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Content asChild>
          <div className={styles.wrapper}>
            <Dialog.Title className={styles.dialogTitle}>
              {intl.formatMessage({ id: 'photo_gallery_full_photo_gallery_title' }, { currentImage: currentSlideIndex + 1, totalImages: numberOfSlides })}
            </Dialog.Title>
            <TopTitleBar.Root className={styles.header}>
              <TopTitleBar.CenterArea title={`${currentSlideIndex + 1}  / ${numberOfSlides}`} />
              <TopTitleBar.RightArea>
                <Dialog.Close asChild>
                  <Button className={styles.closeButton} variant="transparentPrimary">
                    {intl.formatMessage({ id: 'close' })}
                  </Button>
                </Dialog.Close>
              </TopTitleBar.RightArea>
            </TopTitleBar.Root>
            <div className={styles.body}>
              <section className={styles.container}>
                <div className={styles.slider} ref={emblaRef}>
                  <div className={styles.sliderContainer}>
                    {media?.map((mediaItem, index) => {
                      if (mediaItem.type === CMSMediaType.VIDEO) {
                        const showCurrentVideo = () => showVideo(mediaItem.link)
                        const isCurrentVideoActive = isEqualVideoLink(activeVideoLink, mediaItem.link)
                        return (
                          <div className={styles.slide} key={mediaItem.src}>
                            <Video
                              className={cn(isCurrentVideoActive ? styles.videoVisible : styles.videoHidden)}
                              link={mediaItem.link || ''}
                              playing={isCurrentVideoActive}
                              onPauseOrEnded={handlePauseOrEnded}
                            />
                            <div
                              className={styles.imageVideoWrapper}
                              style={{ display: isCurrentVideoActive ? 'none' : 'block' }}
                            >
                              <button
                                className={styles.buttonOverlay}
                                onClick={showCurrentVideo}
                                aria-label={intl.formatMessage({ id: 'photo_gallery_play_video' })}
                                tabIndex={-1}
                              />
                              <Image
                                src={mediaItem.src}
                                srcSet={[1280, 480]}
                                alt={mediaItem.alt || ''}
                                loading={!slidesInView.includes(index) ? 'lazy' : 'eager'}
                              />
                              <IconButton
                                className={styles.playIconButton}
                                glyph="PlayFilled"
                                variant="overlay-neutral"
                                aria-label={intl.formatMessage({ id: 'photo_gallery_play_video' })}
                                size="M"
                              />
                            </div>
                          </div>
                        )
                      }

                      return (
                        <div
                          className={cn(styles.slide, zoomScale > MIN_ZOOM && styles.slideZoomed)}
                          key={mediaItem.src}
                          ref={index === currentSlideIndex ? slideRef : undefined}
                        >
                          <div
                            className={styles.zoomImageWrapper}
                            style={{ '--_zoom-scale': zoomScale } as React.CSSProperties}
                          >
                            <Image
                              src={mediaItem.src}
                              srcSet={[1280, 480]}
                              alt={mediaItem.alt || ''}
                              loading={!slidesInView.includes(index) ? 'lazy' : 'eager'}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </section>
              {numberOfSlides > 0 && (
                <>
                  <IconButton
                    className={cn(styles.navButton, styles.leftButton)}
                    variant="secondary"
                    onClick={() => navigate('previous')}
                    disabled={currentSlideIndex === 0}
                    glyph="ChevronLeft"
                    aria-label={intl.formatMessage({ id: 'photo_gallery_previous_photo' })}
                  />
                  <IconButton
                    className={cn(styles.navButton, styles.rightButton)}
                    variant="secondary"
                    onClick={() => navigate('next')}
                    disabled={currentSlideIndex === numberOfSlides - 1}
                    glyph="ChevronRight"
                    aria-label={intl.formatMessage({ id: 'photo_gallery_next_photo' })}
                  />
                </>
              )}
              {(zoom && activeSlide?.type === CMSMediaType.IMAGE) && (
                <div className={styles.zoomContainer}>
                  <IconButton
                    className={cn(styles.navButton, styles.zoomButton)}
                    variant="secondary"
                    onClick={toggleZoomIn}
                    glyph="Plus"
                    aria-label={intl.formatMessage({ id: 'photo_gallery_zoom_in' })}
                    disabled={zoomScale === MAX_ZOOM}
                  />
                  <IconButton
                    className={cn(styles.navButton, styles.zoomButton)}
                    variant="secondary"
                    onClick={toggleZoomOut}
                    glyph="Minus"
                    aria-label={intl.formatMessage({ id: 'photo_gallery_zoom_out' })}
                    disabled={zoomScale === MIN_ZOOM}
                  />
                </div>
              )}
            </div>
            <div className={styles.footer} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function isEqualVideoLink(activeVideoLink: string | undefined, currentVideoLink: string | undefined) {
  return Boolean(activeVideoLink) && activeVideoLink === currentVideoLink
}
